'use client'

import { Menu } from 'lucide-react'
import Link from 'next/link'
import { useSelectedLayoutSegment } from 'next/navigation'
import * as React from 'react'

import { cn } from '@/lib/utils'
import { useBranding, useBrandingHostname } from '@/hooks/use-branding'
import useLocale from '@/hooks/use-locale'
import useMediaQuery from '@/hooks/use-media-query'

import { Icons } from '@/components/icons'
import { ForumNavUnreadIndicator } from '@/components/layouts/nav/forum-nav-unread-indicator'
import HeaderRightActions from '@/components/layouts/nav/header-right-actions'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'

import { filterMainNavByDomain, FORUM_NAV_HREF, getMainNavConfigs } from '@/configs/router'

import { HeaderConfig } from '@/types/header-footer-config'
import { MainNavItem } from '@/types/nav.type'

interface MobileNavContentProps {
  headerConfig?: Required<HeaderConfig>
}

const mobileNavLinkClassName =
  'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-foreground/80 transition-colors hover:bg-muted/60 hover:text-foreground'

export default function MobileNavContent({ headerConfig }: MobileNavContentProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const segment = useSelectedLayoutSegment()
  const [open, setOpen] = React.useState(false)
  const { text, lang } = useLocale()
  const { brandHref, hasImageLogo, isFsdsDomain, logoElement, siteName } = useBranding()
  const hostname = useBrandingHostname()
  const mainNavConfigs = React.useMemo(
    () => filterMainNavByDomain(headerConfig?.mobileNavItems ?? getMainNavConfigs(text, lang), isFsdsDomain, hostname),
    [headerConfig?.mobileNavItems, text, lang, isFsdsDomain, hostname]
  )
  const rightActions = headerConfig?.rightActions ?? []

  if (isDesktop) return null

  const RecursiveAccordionItem = ({
    item,
    segment,
    setOpen,
    level = 0,
  }: {
    item: MainNavItem
    segment: string
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    level?: number
  }) => {
    const Icon = item.icon ? Icons[item.icon] : null

    if (!item.items?.length) {
      const isForumLink = item.href === FORUM_NAV_HREF

      return (
        <MobileLink
          href={item.href ?? '/'}
          segment={segment}
          setOpen={setOpen}
          disabled={item.disabled}
          className={cn(
            mobileNavLinkClassName,
            level > 0 && 'ml-3',
            isForumLink && 'relative',
            item.disabled && 'cursor-not-allowed opacity-60'
          )}
        >
          {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" />}
          <span>{item.title}</span>
          {isForumLink && <ForumNavUnreadIndicator className="ml-auto" />}
        </MobileLink>
      )
    }

    return (
      <AccordionItem value={`${item.title}-${level}`} className={cn('border-none', level > 0 && 'ml-2')}>
        <AccordionTrigger
          className={cn(
            'rounded-xl px-3 py-2.5 text-sm font-medium hover:no-underline hover:bg-muted/60 [&[data-state=open]]:bg-muted/40',
            level > 0 && 'text-foreground/80'
          )}
        >
          <div className="flex items-center gap-3">
            {Icon && <Icon className="h-4 w-4 shrink-0 text-primary" />}
            <span className="text-start">{item.title}</span>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pb-1 pt-1">
          <div className="flex flex-col gap-1 border-l border-border/60 pl-3 ml-2">
            {item.items.map((subItem, index) => (
              <RecursiveAccordionItem
                key={index}
                item={subItem}
                segment={segment}
                setOpen={setOpen}
                level={level + 1}
              />
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 items-center lg:hidden">
      <div className="flex min-w-0 items-center gap-2">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="h-10 w-10 shrink-0 rounded-xl border-border/60 bg-background/80 shadow-sm hover:bg-muted/60"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5" aria-hidden="true" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="flex w-full flex-col gap-0 border-r border-border/60 p-0 sm:max-w-xs">
          <div className="border-b border-border/60 bg-muted/20 px-5 py-4">
            <Link
              href={brandHref}
              className="flex items-center gap-2 rounded-xl transition-colors hover:bg-muted/40 -mx-1 px-1 py-0.5"
              onClick={() => setOpen(false)}
              aria-label="Go to Home page"
            >
              <span className="flex shrink-0 items-center [&_img]:h-9 [&_img]:w-auto [&_img]:max-w-[9.5rem] [&_svg]:h-9 [&_svg]:w-auto">
                {logoElement}
              </span>
              {hasImageLogo && (
                <span className="min-w-0 truncate font-semibold tracking-tight">{siteName}</span>
              )}
            </Link>
          </div>
          <ScrollArea className="flex-1 px-3 py-4">
            <div className="pr-2">
              <Accordion type="multiple" className="flex w-full flex-col gap-1">
                {mainNavConfigs?.map((item, index) => (
                  <RecursiveAccordionItem key={index} item={item} segment={String(segment)} setOpen={setOpen} />
                ))}
              </Accordion>
              {rightActions.length > 0 && (
                <>
                  <Separator className="my-4" />
                  <HeaderRightActions actions={rightActions} className="flex-col items-stretch gap-2" />
                </>
              )}
            </div>
          </ScrollArea>
          </SheetContent>
        </Sheet>
        <Link
          href={brandHref}
          className="flex min-w-0 items-center gap-2 rounded-lg px-1 py-0.5 transition-colors hover:bg-muted/40"
          aria-label="Go to Home page"
        >
          <span className="flex shrink-0 items-center [&_img]:h-8 [&_img]:w-auto [&_img]:max-w-[8.75rem] [&_svg]:h-8 [&_svg]:w-auto">
            {logoElement}
          </span>
          {hasImageLogo && (
            <span className="min-w-0 truncate text-sm font-semibold tracking-tight">{siteName}</span>
          )}
        </Link>
      </div>
    </div>
  )
}

interface MobileLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string
  disabled?: boolean
  segment: string
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
}

function MobileLink({ children, href, disabled, segment, setOpen, className, ...props }: MobileLinkProps) {
  const isActive = href.includes(segment) && segment.length > 0

  return (
    <Link
      href={href}
      className={cn(
        mobileNavLinkClassName,
        isActive && 'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
        disabled && 'pointer-events-none opacity-60',
        className
      )}
      onClick={() => setOpen(false)}
      {...props}
    >
      {children}
    </Link>
  )
}
